<script setup lang="ts">
import { computed } from 'vue';

import type { Location } from '@/entities/auth';

import { isLocationClosed, isLocationPublic } from '@/entities/location';
import { AppStatusChip } from '@/shared/ui';
import { IconPreloader } from '@/shared/ui/icons';

interface Props {
  location: Location;
  isSelected?: boolean;
  isLoading?: boolean;
}

const props = defineProps<Props>();

const isClosed = computed(() => isLocationClosed(props.location));

const emit = defineEmits<{
  select: [location: Location];
}>();
</script>

<template>
  <button
    type="button"
    class="ml-[20px] min-h-11 cursor-pointer rounded-[6px] border p-[10px] text-left transition-colors focus-visible:outline-2 focus-visible:outline-(--accent) disabled:cursor-wait"
    :class="{
      'opacity-40': isLoading && !isSelected,
      'bg-(--surface-sunken)': isSelected,
      'bg-(--surface-card) hover:bg-(--surface-hover)': !isSelected,
    }"
    :style="{
      borderColor: 'var(--border)',
    }"
    :aria-current="isSelected ? 'true' : undefined"
    :disabled="isLoading"
    @click="emit('select', location)"
  >
    <div class="flex justify-between">
      <div class="flex flex-col gap-y-[4px]">
        <div class="flex items-center gap-x-2">
          <div
            class="text-sm"
            style="color: var(--text-primary)"
          >
            {{ location.title }}
          </div>
          <AppStatusChip
            v-if="isClosed"
            tone="neutral"
          >
            Закрыта
          </AppStatusChip>
          <span
            v-else-if="!isLocationPublic(location)"
            class="text-xs text-(--text-secondary)"
            >Скрыта из онлайн-записи</span
          >
        </div>
        <div
          v-if="location.full_address"
          class="text-xs"
          style="color: var(--text-secondary)"
        >
          {{ location.full_address }}
        </div>
      </div>
      <div class="flex items-center pr-[12px]">
        <IconPreloader
          v-if="isSelected"
          class="text-(--accent)"
        />
      </div>
    </div>
  </button>
</template>
